import { useEffect, useState } from "react";

import { Slider } from "@/app/components/ui/slider";

interface ProficiencyRangeProps {
	setLevel: (level: string) => void;
	value: string;
}

/**
 * The five levels, in track order. `legacyValue` is what an older build of this
 * slider wrote for the same level; accept it so a saved level restores the thumb.
 */
const PROFICIENCY_LEVELS = [
	{ value: "Elementary Proficiency", caption: ["Elementary", "Proficiency"] },
	{ value: "Limited Working Proficiency", legacyValue: "Limited working Proficiency", caption: ["Limited", "working"] },
	{ value: "Professional Working Proficiency", caption: ["Professional", "working"] },
	{ value: "Full Professional Proficiency", caption: ["Full", "Professional"] },
	{ value: "Native / Bilingual Proficiency", caption: ["Native/", "Bilingual"] },
];

/** Matches `size-3` on the slider thumb. */
const THUMB_SIZE = 12;

/**
 * Where the thumb comes to rest on stop `index`, measured from the left of the
 * track. Radix keeps the thumb inside the track, so it stops half a thumb in at
 * the first stop and half a thumb short at the last; the dots take the same nudge
 * to stay under it. Reading off the track rather than off the caption grid keeps
 * the dots on the thumb at both widths the track is given below.
 */
const stopPosition = (index: number) => {
	const percent = (index / (PROFICIENCY_LEVELS.length - 1)) * 100;
	return `calc(${percent}% + ${THUMB_SIZE / 2 - (percent / 100) * THUMB_SIZE}px)`;
};

/**
 * Proficiency slider. One step per level, so the thumb lands on a level rather
 * than between two.
 */
function ProficiencyRange({ setLevel, value }: ProficiencyRangeProps) {
	const [levelIndex, setLevelIndex] = useState(0);
	const lastIndex = PROFICIENCY_LEVELS.length - 1;

	const selectLevel = (index: number) => {
		setLevelIndex(index);
		setLevel(PROFICIENCY_LEVELS[index].value);
	};

	useEffect(() => {
		// The track paints a thumb at Elementary from the very first render, so a
		// reader who is happy with that level never moves it and never fires
		// `onValueChange`. The stored proficiency stayed empty while the control
		// showed a choice, and the save refused with "Pick a language and a
		// proficiency" pointing at a field that looked filled in. Adopt what is
		// already on screen instead of letting the two disagree.
		if (!value) {
			setLevelIndex(0);
			setLevel(PROFICIENCY_LEVELS[0].value);
			return;
		}

		const savedIndex = PROFICIENCY_LEVELS.findIndex((level) => level.value === value || level.legacyValue === value);
		if (savedIndex !== -1) setLevelIndex(savedIndex);
	}, [value]);

	return (
		<div>
			{/* Two widths, each matched by the caption row below. The default is the one
			    that reads captions off five equal columns, so the track gives up half a
			    column at each end — 10%, less the half thumb Radix keeps inside the
			    track. That half column is the price of centred end captions and cannot be
			    reclaimed: a caption centred on a stop at the very edge of the track needs
			    half its width beyond that edge, where the container clips it.

			    Below `lg` the track takes the full width instead and the end captions
			    read off the ends of the track. `lg` because that is where the page itself
			    stops filling the screen (`w-full lg:max-w-2xl`), so the track widens on
			    exactly the screens the column is already full width on. Written as an
			    override on the narrow side rather than the wide one so that the aligned
			    layout is what a missing stylesheet rule falls back to — the mixed state
			    (narrow captions against an inset track) is the one that looks broken.

			    The padding belongs to the slider rather than to a wrapper: Radix
			    measures this element as the pressable band, so a press that lands near a
			    dot instead of on the 4px track still moves the thumb. */}
			<div className="relative mx-[calc(10%-6px)] max-lg:mx-0">
				<Slider
					className="py-2"
					min={0}
					max={lastIndex}
					step={1}
					value={[levelIndex]}
					onValueChange={([index]) => selectLevel(index)}
				/>
				{/* Transparent to the pointer, so pressing a dot reaches the slider under
				    it — Radix snaps to the nearest step, which is the dot pressed. */}
				<div className="pointer-events-none absolute inset-0">
					{PROFICIENCY_LEVELS.map((level, index) => (
						<span
							className={`-translate-x-1/2 -translate-y-1/2 absolute top-1/2 size-2 rounded-full ${levelIndex === index ? "bg-transparent" : "border border-gray-300 bg-white"}`}
							style={{ left: stopPosition(index) }}
							key={level.value}
						/>
					))}
				</div>
			</div>
			{/* Five equal columns, so a caption centres on the dot above it.
			    Half-width end columns on a phone instead: the first and last stop sit on
			    the ends of a full-width track there, so those captions read off the track
			    edge while the middle three still centre on their stop. The 6px inset
			    matches the half thumb the end stops keep from the track edge.
			    `minmax(0,…)` lets a column hold less than its longest word — plain `1fr`
			    widens the column instead, which shifts every column after it and pushes
			    the row off the screen. */}
			<div className="mt-1 grid grid-cols-5 text-gray-600 text-xs max-lg:mx-1.5 max-lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)] max-sm:text-[10px]">
				{PROFICIENCY_LEVELS.map((level, index) => (
					<button
						type="button"
						key={level.value}
						onClick={() => selectLevel(index)}
						// The caption is two words of a level name, not a sentence; read out
						// the level it picks instead.
						aria-label={level.value}
						// `block p-0` overrides `button:not([data-slot])` in index.css, which
						// makes every plain button a padded flex row. As a flex row the caption
						// sat at the left of its column whatever `text-align` said, and the
						// padding pushed the end captions off the ends of the track.
						className={`block cursor-pointer p-0 text-center hover:text-gray-900 ${
							index === 0 ? "max-lg:text-left" : index === lastIndex ? "max-lg:text-right" : ""
						} ${levelIndex === index ? "text-gray-900" : ""}`}
					>
						{level.caption[0]}
						<br />
						{level.caption[1]}
					</button>
				))}
			</div>
		</div>
	);
}

export default ProficiencyRange;
